/**
 * API client de Compras (Fase 7).
 *
 *   GET    /api/fornecedores
 *   POST   /api/fornecedores
 *   PATCH/DELETE /api/fornecedores/{id}
 *
 *   GET    /api/requisicoes
 *   POST   /api/requisicoes
 *   GET    /api/requisicoes/{id}
 *   POST   /api/requisicoes/{id}/aprovar
 *   POST   /api/requisicoes/{id}/cancelar
 *
 *   GET    /api/ordens-compra
 *   POST   /api/ordens-compra                                    (avulsa)
 *   POST   /api/ordens-compra/criar-de-requisicao/{requisicao_id}
 *   POST   /api/ordens-compra/{id}/registrar-entrada
 *   POST   /api/ordens-compra/{id}/cancelar
 */

const PREF_F = "/api/fornecedores";
const PREF_R = "/api/requisicoes";
const PREF_O = "/api/ordens-compra";

// ---------- Fornecedor ----------

export function listarFornecedores(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("busca", params.busca);
  if (params.ativo !== undefined) usp.set("ativo", params.ativo ? "true" : "false");
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREF_F}${qs ? `?${qs}` : ""}`);
}

export function obterFornecedor(request, id) {
  return request(`${PREF_F}/${id}`);
}

export function criarFornecedor(request, dados) {
  return request(PREF_F, { method: "POST", body: JSON.stringify(dados) });
}

export function atualizarFornecedor(request, id, dados) {
  return request(`${PREF_F}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}

export function deletarFornecedor(request, id) {
  return request(`${PREF_F}/${id}`, { method: "DELETE" });
}

// ---------- Requisicao ----------

export function listarRequisicoes(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.prioridade) usp.set("prioridade", params.prioridade);
  if (params.origem) usp.set("origem", params.origem);
  if (params.listaCompras) usp.set("lista_compras", params.listaCompras);
  if (params.busca) usp.set("busca", params.busca);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  // ADR-0069: filtros da fila do comprador.
  if (params.comprador) usp.set("comprador", params.comprador);
  if (params.atrasadas) usp.set("atrasadas", "1");
  if (params.semAnexo) usp.set("sem_anexo", "1");
  if (params.prazoAte) usp.set("prazo_ate", params.prazoAte);
  if (params.criadaDesde) usp.set("criada_desde", params.criadaDesde);
  if (params.ordering) usp.set("ordering", params.ordering);
  const qs = usp.toString();
  return request(`${PREF_R}${qs ? `?${qs}` : ""}`);
}

// ============================================================
// Fila do comprador (ADR-0069, 2026-09-14). Espelha compras/views_fila.py.
// ============================================================

/** KPIs da fila (atrasadas, aguardando aprovação, tempo p/ cotar, ...). */
export function resumoRequisicoes(request) {
  return request(`${PREF_R}/resumo`, { silentError: true });
}

/** Linha do tempo = auditoria traduzida. `params`: {tipos:[], desde, ate, limite}. */
export function eventosRequisicoes(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.tipos?.length) usp.set("tipos", params.tipos.join(","));
  if (params.desde) usp.set("desde", params.desde);
  if (params.ate) usp.set("ate", params.ate);
  if (params.limite) usp.set("limite", String(params.limite));
  const qs = usp.toString();
  return request(`${PREF_R}/eventos${qs ? `?${qs}` : ""}`, { silentError: true });
}

export function eventosDaRequisicao(request, id) {
  return request(`${PREF_R}/${id}/eventos`, { silentError: true });
}

/**
 * Arraste do quadro / botões do drawer. Responde 409 `{precisa: "motivo"|"oc"}`
 * quando a tela precisa abrir um modal e reenviar — por isso `silentError`.
 */
export function moverRequisicaoPara(request, id, situacao, { motivo = "", observacao = "" } = {}) {
  return request(`${PREF_R}/${id}/mover-para`, {
    method: "POST",
    body: JSON.stringify({ situacao, motivo, observacao }),
    silentError: true,
  });
}

export function iniciarCotacaoRequisicao(request, id) {
  return request(`${PREF_R}/${id}/iniciar-cotacao`, { method: "POST" });
}

export function atribuirComprador(request, id, compradorId) {
  return request(`${PREF_R}/${id}/atribuir-comprador`, {
    method: "POST",
    body: JSON.stringify({ comprador: compradorId || null }),
  });
}

/** `acao`: aprovar | cancelar | excluir | atribuir. Resposta: {resultados, ok, falhas}. */
export function loteRequisicoes(request, acao, ids, { motivo = "", compradorId = null } = {}) {
  return request(`${PREF_R}/lote`, {
    method: "POST",
    body: JSON.stringify({ acao, ids, motivo, comprador: compradorId }),
    silentError: true,
  });
}

/** PATCH de cabeçalho: {prioridade?, prazo_cotacao?, justificativa?}. */
export function atualizarRequisicao(request, id, dados) {
  return request(`${PREF_R}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}

/** Apaga de verdade (só rascunho, em andamento sem OC ou cancelada). */
export function excluirRequisicao(request, id) {
  return request(`${PREF_R}/${id}`, { method: "DELETE", silentError: true });
}

// ---- Cotação por item (D5) ----

export function removerCotacao(request, requisicaoId, cotacaoId) {
  return request(`${PREF_R}/${requisicaoId}/cotacoes/${cotacaoId}`, { method: "DELETE" });
}

export function escolherCotacaoVencedora(request, requisicaoId, cotacaoId) {
  return request(`${PREF_R}/${requisicaoId}/cotacoes/${cotacaoId}/vencedora`, { method: "POST" });
}

/**
 * Cotação conjunta: um fornecedor para vários itens, com UM frete (rateado
 * pelo valor de cada item) e um prazo geral; tudo ou nada.
 * `dados`: { fornecedor, prazo_dias, frete_total, observacao,
 *            linhas: [{ item, valor_unitario, nao_tem, prazo_dias }] }.
 */
export function cotarEmConjunto(request, requisicaoId, dados) {
  return request(`${PREF_R}/${requisicaoId}/cotacao-conjunta`, {
    method: "POST",
    body: JSON.stringify(dados),
    silentError: true,
  });
}

/** O fornecedor vence os itens em que tem valor; o resto volta em `vencedor_em_lote.ignorados`. */
export function escolherVencedorEmLote(request, requisicaoId, fornecedor, itens) {
  return request(`${PREF_R}/${requisicaoId}/vencedor-em-lote`, {
    method: "POST",
    body: JSON.stringify({ fornecedor, itens }),
    silentError: true,
  });
}

/** Arquivo de um anexo do RQC, pelo proxy autenticado da requisição. */
export function urlArquivoAnexoRqc(requisicaoId, anexoId) {
  return `${PREF_R}/${requisicaoId}/rqc-anexos/${anexoId}/arquivo`;
}

/** Uma OC por fornecedor vencedor. `itens` opcional restringe o subconjunto. */
export function gerarOcsDosVencedores(request, requisicaoId, itens = null) {
  return request(`${PREF_R}/${requisicaoId}/gerar-ocs-vencedores`, {
    method: "POST",
    body: JSON.stringify(itens ? { itens } : {}),
  });
}

// ---- Comentários e pessoas (D6) ----

export function listarComentarios(request, requisicaoId) {
  return request(`${PREF_R}/${requisicaoId}/comentarios`, { silentError: true });
}

export function comentarRequisicao(request, requisicaoId, texto, mencionados = []) {
  return request(`${PREF_R}/${requisicaoId}/comentarios`, {
    method: "POST",
    body: JSON.stringify({ texto, mencionados }),
  });
}

/** Quem pode ser comprador ou mencionado (usuários da unidade com compras:view). */
export function pessoasCompras(request, busca = "") {
  const qs = busca ? `?busca=${encodeURIComponent(busca)}` : "";
  return request(`${PREF_R}/pessoas${qs}`, { silentError: true });
}

/** Produtos para a Nova requisição, com saldo, mínimo e último preço. */
export function produtosParaRequisicao(request, { busca = "", abaixoMinimo = false } = {}) {
  const usp = new URLSearchParams();
  if (busca) usp.set("busca", busca);
  if (abaixoMinimo) usp.set("abaixo_minimo", "1");
  const qs = usp.toString();
  return request(`${PREF_R}/produtos${qs ? `?${qs}` : ""}`, { silentError: true });
}

export function obterRequisicao(request, id) {
  return request(`${PREF_R}/${id}`);
}

export function criarRequisicao(request, dados) {
  return request(PREF_R, { method: "POST", body: JSON.stringify(dados) });
}

export function aprovarRequisicao(request, id, aprovacoes = []) {
  return request(`${PREF_R}/${id}/aprovar`, {
    method: "POST",
    body: JSON.stringify({ aprovacoes }),
  });
}

/**
 * Fecha a requisicao SEM ordem de compra ("Atendida" pelo arrasto no quadro).
 * O caminho com rastreabilidade completa e criar a OC — este e o atalho para
 * o que foi resolvido por fora (dinheiro, doacao, material que ja estava aqui).
 */
export function atenderRequisicao(request, id, observacao = "") {
  return request(`${PREF_R}/${id}/atender`, {
    method: "POST",
    body: JSON.stringify({ observacao }),
  });
}

/** Volta a requisição para APROVADA ou RASCUNHO (de ATENDIDA/CANCELADA/APROVADA). */
export function reabrirRequisicao(request, id, situacao, motivo) {
  return request(`${PREF_R}/${id}/reabrir`, {
    method: "POST",
    body: JSON.stringify({ situacao, motivo }),
  });
}

export function cancelarRequisicao(request, id, motivo) {
  return request(`${PREF_R}/${id}/cancelar`, {
    method: "POST",
    body: JSON.stringify({ motivo: motivo || "" }),
  });
}

// ---------- OrdemCompra ----------

export function listarOrdensCompra(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.situacao) usp.set("situacao", params.situacao);
  if (params.fornecedor) usp.set("fornecedor", params.fornecedor);
  if (params.busca) usp.set("busca", params.busca);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREF_O}${qs ? `?${qs}` : ""}`);
}

export function obterOrdemCompra(request, id) {
  return request(`${PREF_O}/${id}`);
}

export function criarOcAvulsa(request, dados) {
  return request(PREF_O, { method: "POST", body: JSON.stringify(dados) });
}

export function criarOcDeRequisicao(request, requisicaoId, dados) {
  return request(`${PREF_O}/criar-de-requisicao/${requisicaoId}`, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function registrarEntrada(request, ocId, dados) {
  return request(`${PREF_O}/${ocId}/registrar-entrada`, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function cancelarOC(request, ocId, motivo) {
  return request(`${PREF_O}/${ocId}/cancelar`, {
    method: "POST",
    body: JSON.stringify({ motivo: motivo || "" }),
  });
}

// ============================================================
// Terceiros (2026-08-04) — fila unica do comprador
// ============================================================

/**
 * Tipos de servico contratado fora. A lista veio do dono do produto e espelha
 * os fornecedores reais da marcenaria — nao inventar valor novo sem combinar.
 */
export const TIPOS_TERCEIRO = [
  { value: "SERRALHERIA", label: "Serralheria" },
  { value: "PERFIL_ALUMINIO_PINTURA", label: "Perfil de alumínio com pintura" },
  { value: "PORTAS_FRENTES_ALUMINIO_VIDRO", label: "Portas e frentes de alumínio com vidro" },
  { value: "VIDRACARIA", label: "Vidraçaria" },
  { value: "TAPECARIA", label: "Tapeçaria" },
  { value: "FERRAGENS_ESPECIAIS", label: "Ferragens especiais" },
];

export const TIPO_TERCEIRO_LABEL = Object.fromEntries(
  TIPOS_TERCEIRO.map((t) => [t.value, t.label]),
);

/** De onde a requisicao nasceu — a etiqueta que separa as filas na mesma tela. */
export const ORIGENS_REQUISICAO = [
  { value: "MANUAL", label: "Manual", tone: "neutral" },
  { value: "FERRAGEM_FALTA", label: "Ferragem em falta", tone: "warning" },
  { value: "TERCEIROS", label: "Terceiros", tone: "info" },
  { value: "LOTE_MDF", label: "Lote de produção", tone: "neutral" },
  // Plano de 2026-09-11: o PharuCut manda o consumo do plano aceito
  // para Compras antes de cortar; o comprador dá por atendida aqui.
  { value: "PHARUCUT", label: "PharuCut", tone: "info" },
  // ADR-0074: formulario do escritorio em Solicitacoes.
  { value: "RQC", label: "Requisição de compras (RQC)", tone: "info" },
];

export const ORIGEM_LABEL = Object.fromEntries(
  ORIGENS_REQUISICAO.map((o) => [o.value, o.label]),
);
export const ORIGEM_TONE = Object.fromEntries(
  ORIGENS_REQUISICAO.map((o) => [o.value, o.tone]),
);

/**
 * Cria a requisicao de compra dos itens de terceiro de uma lista de ferragens.
 * O anexo NAO vai aqui: sobe depois, um por item, via `anexarArquivoEmItem` —
 * o padrao multipart do projeto e sempre 1 arquivo por request.
 */
export function criarRequisicaoTerceiros(request, listaComprasId, itens) {
  return request(`${PREF_R}/terceiros`, {
    method: "POST",
    body: JSON.stringify({
      lista_compras_id: listaComprasId,
      itens: itens.map(({ descricao, tipo_terceiro, observacao }) => ({
        descricao, tipo_terceiro, observacao,
      })),
    }),
    silentError: true,
  });
}

/** Sobe o detalhamento de UM item de terceiro (multipart). */
export function anexarArquivoEmItem(request, requisicaoId, itemId, arquivo) {
  const formData = new FormData();
  formData.append("arquivo", arquivo);
  return request(`${PREF_R}/${requisicaoId}/itens/${itemId}/anexo`, {
    method: "POST",
    body: formData,
    headers: {},  // sem Content-Type: o browser define o boundary
    silentError: true,
  });
}

// ------------------------------------------ fila do comprador (ADR-0078)

/** Uma linha por item de toda a fila (tabela "por item"). */
export function listarItensDaFila(request, { incluirCanceladas = false } = {}) {
  return request(`${PREF_R}/itens${incluirCanceladas ? "?incluir_canceladas=1" : ""}`, { silentError: true });
}

/** Tudo das requisições `ids` (datas de cada etapa, itens, cotações, OCs,
 *  recebimentos e histórico) para a exportação completa. POST: a lista da
 *  tela não cabe numa URL. */
export function dadosParaExportacao(request, ids) {
  return request(`${PREF_R}/exportacao`, {
    method: "POST",
    body: JSON.stringify({ ids }),
    silentError: true,
  });
}

/** Anotação de entrega/retirada e obra do item. Campo ausente = não mexe. */
export function editarItemRequisicao(request, requisicaoId, itemId, dados) {
  return request(`${PREF_R}/${requisicaoId}/itens/${itemId}`, {
    method: "PATCH", body: JSON.stringify(dados), silentError: true,
  });
}

/** Sobe UM anexo do item (até 5 por item, qualquer tipo de item). */
export function anexarNoItem(request, requisicaoId, itemId, arquivo) {
  const formData = new FormData();
  formData.append("arquivo", arquivo);
  return request(`${PREF_R}/${requisicaoId}/itens/${itemId}/anexos`, {
    method: "POST", body: formData, headers: {}, silentError: true,
  });
}

export function removerAnexoDoItem(request, requisicaoId, itemId, anexoId) {
  return request(`${PREF_R}/${requisicaoId}/itens/${itemId}/anexos/${anexoId}`, {
    method: "DELETE", silentError: true,
  });
}

/** Devolve à requisição `devolvidaId` os itens que vieram dela. */
export function desfazerConsolidacao(request, principalId, devolvidaId) {
  return request(`${PREF_R}/${principalId}/desfazer-consolidacao`, {
    method: "POST", body: JSON.stringify({ requisicao: devolvidaId }), silentError: true,
  });
}

/** Unidades do item não cadastrado — espelha `estoque.models.UNIDADES`
 *  (o backend valida; aqui é só o que a tela oferece). */
export const UNIDADES_DE_COMPRA = [
  { value: "UN", label: "Unidade" }, { value: "PC", label: "Peça" }, { value: "CX", label: "Caixa" },
  { value: "PAR", label: "Par" }, { value: "RL", label: "Rolo" }, { value: "CH", label: "Chapa inteira" },
  { value: "M", label: "Metro" }, { value: "M2", label: "Metro quadrado" }, { value: "M3", label: "Metro cúbico" },
  { value: "KG", label: "Quilograma" }, { value: "G", label: "Grama" },
  { value: "L", label: "Litro" }, { value: "ML", label: "Mililitro" },
];

export const MAX_ANEXOS_POR_ITEM = 5;
export const EXTENSOES_DE_ANEXO = ".pdf,.jpg,.jpeg,.png,.heic,.dwg";

/** Move o card de coluna no quadro do comprador (camada visual, nao muda situacao). */
export function moverEtapaRequisicao(request, id, etapa) {
  return request(`${PREF_R}/${id}/mover-etapa`, {
    method: "POST",
    body: JSON.stringify({ etapa }),
    silentError: true,
  });
}

/** Colunas do quadro do comprador (camada visual sobre as situacoes). */
export function obterColunasKanbanCompras(request) {
  return request("/api/compras/colunas-kanban");
}

export function salvarColunasKanbanCompras(request, colunas) {
  return request("/api/compras/colunas-kanban", {
    method: "PUT",
    body: JSON.stringify({ colunas }),
    silentError: true,
  });
}

// ============================================================
// Plano de Suprimentos (Fase 5): status por item (D36), OC parcial e
// preço por fornecedor (D34). Espelha compras/urls.py.
// ============================================================

/** Situação de um item da requisição: PENDENTE | EM_COTACAO | COMPRADO | CANCELADO. */
export function marcarSituacaoItem(request, requisicaoId, itemId, situacao, observacao = "") {
  return request(`${PREF_R}/${requisicaoId}/itens/${itemId}/situacao`, {
    method: "POST",
    body: JSON.stringify({ situacao, observacao }),
  });
}

// Mesmo vocabulario da requisicao: ela vale a etapa do item MAIS ATRASADO
// (backend `compras/etapas.py`), entao item e requisicao falam a mesma lingua.
// APROVADO so e escrito pela aprovacao da requisicao — nunca pelo controle do item.
export const SITUACOES_ITEM_REQ = [
  { value: "PENDENTE", label: "A cotar", tone: "neutral" },
  { value: "EM_COTACAO", label: "Em andamento", tone: "warning" },
  { value: "APROVADO", label: "Aprovado", tone: "violet" },
  { value: "COMPRADO", label: "Comprado", tone: "success" },
  { value: "CANCELADO", label: "Cancelado", tone: "danger" },
];
export const SITUACAO_ITEM_LABEL = Object.fromEntries(SITUACOES_ITEM_REQ.map((s) => [s.value, s.label]));
export const SITUACAO_ITEM_TONE = Object.fromEntries(SITUACOES_ITEM_REQ.map((s) => [s.value, s.tone]));

const PREF_PF = "/api/produto-fornecedores";

export function listarProdutoFornecedores(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.produto) usp.set("produto", params.produto);
  if (params.fornecedor) usp.set("fornecedor", params.fornecedor);
  usp.set("page_size", "500");
  return request(`${PREF_PF}?${usp.toString()}`);
}

export function definirProdutoFornecedor(request, dados) {
  return request(PREF_PF, { method: "POST", body: JSON.stringify(dados) });
}

export function atualizarProdutoFornecedor(request, id, dados) {
  return request(`${PREF_PF}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}

/** Soft: o backend marca ativo=false e tira o preferencial. */
export function removerProdutoFornecedor(request, id) {
  return request(`${PREF_PF}/${id}`, { method: "DELETE" });
}

/** Vincula um fornecedor a vários produtos de uma vez (ADR-0070). Idempotente. */
export function vincularFornecedorLote(request, produtoIds, fornecedorId) {
  return request(`${PREF_PF}/lote`, { method: "POST", body: JSON.stringify({ produtos: produtoIds, fornecedor: fornecedorId }) });
}
